import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { formatMoney } from '@dorixona/utils'
import { BackButton } from '../../../ui/BackButton'
import { Icon } from '../../../ui/Icon'
import { Failed, Loading } from '../../../ui/Load'
import { useCurrentLocation } from '../../../app/location'
import { useStores } from '../../stores/api'
import { StoresOnMap } from '../../stores/StoresOnMap'
import { useSelectionMatch } from '../api'
import { rankStores } from '../model'
import { useSelection } from '../selection'

/**
 * «Где есть всё» — только аптеки, где есть каждое выбранное лекарство в
 * нужном количестве; открытые сейчас выше, среди них ближние, потом дешевле.
 * В строке — сумма за всё по ценам этой аптеки. Такой аптеки нет — так и
 * сказано, а ниже видно, в скольких аптеках каждое: «узкое место» помечено,
 * его можно убрать прямо здесь.
 */
export function MultiSearchStoresScreen() {
  const { t } = useTranslation(['catalog', 'common'])
  const navigate = useNavigate()
  const selection = useSelection()
  const stock = useSelectionMatch(selection.items)
  const stores = useStores()
  // Точку не спрашиваем сами: она появляется, когда покупатель нажал «Где я».
  const here = useCurrentLocation(false)
  const { result } = stock

  const toSearch = () => {
    void navigate('/search')
  }

  return (
    <section className="screen checkout-wrap ms-stores">
      <div className="mhead">
        <BackButton to="/search" />
        <h1>{t('catalog:multiSearch.whereAll')}</h1>
      </div>

      {selection.items.length === 0 ? (
        <div className="card empty">
          <Icon name="i-pill" size={54} />
          <div className="t">{t('catalog:multiSearch.empty')}</div>
          {t('catalog:multiSearch.emptyHint')}
          <div className="ms-empty-action">
            <Button onPress={toSearch}>{t('catalog:multiSearch.toSearch')}</Button>
          </div>
        </div>
      ) : null}

      {stock.pending || stores.isPending ? (
        <Loading what={t('common:state.loading')} count={3} />
      ) : null}

      {stock.error ? <Failed error={stock.error} onRetry={stock.retry} /> : null}
      {stores.isError ? (
        <Failed
          error={stores.error}
          onRetry={() => {
            void stores.refetch()
          }}
        />
      ) : null}

      {result?.kind === 'none' ? (
        <>
          <div className="card ms-verdict" role="status">
            <div className="t">{t('catalog:multiSearch.noneHasAll')}</div>
            <p>
              {t(
                result.fewest.stores > 0
                  ? 'catalog:multiSearch.bottleneck'
                  : 'catalog:multiSearch.missing',
                { name: result.fewest.item.name },
              )}
            </p>
          </div>
          <h2 className="ms-title">{t('catalog:multiSearch.perItem')}</h2>
          <ul className="card ms-list">
            {result.counts.map(({ item, stores: count }) => (
              <li
                key={item.id}
                className={item.id === result.fewest.item.id ? 'ms-picked ms-fewest' : 'ms-picked'}
              >
                <span className="ms-main">
                  <span className="t1">{item.name}</span>
                  <span className={count > 0 ? 't2 ms-ok' : 't2 ms-none'}>
                    {count > 0
                      ? t('catalog:multiSearch.inStores', { count })
                      : t('catalog:multiSearch.inNoStore')}
                    {item.id === result.fewest.item.id ? (
                      <b className="ms-flag"> · {t('catalog:multiSearch.fewest')}</b>
                    ) : null}
                  </span>
                </span>
                <Press
                  squish="icon"
                  className="ms-remove"
                  aria-label={t('catalog:multiSearch.remove', { name: item.name })}
                  onPress={() => {
                    selection.remove(item.id)
                  }}
                >
                  <Icon name="i-x" size={16} />
                </Press>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {result?.kind === 'all' && stores.data ? (
        <StoresOnMap
          rows={rankStores(result.stores, stores.data, here.data ?? null).map((row) => ({
            store: row.store,
            name: row.storeName,
            side: (
              <>
                <b className="num">{formatMoney(row.total)}</b>
                <span className="ms-for">{t('catalog:multiSearch.forAll')}</span>
              </>
            ),
          }))}
        />
      ) : null}
    </section>
  )
}
